import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  sendDeleteRequest,
  sendGetRequest,
  sendPatchRequest,
  sendPostRequest,
} from '../../../api/requestUtil';

type AdminContactUsListResponse = {
  count: number;
  next: string | null;
  previous: string | null;
  results: AdminContactUsType[];
  page_size: number;
  unread_count: number;
};

type ToggleReadResponse = {
  detail: string;
  submission: AdminContactUsType;
};

export const getAdminContactUsList = createAsyncThunk<
  AdminContactUsListResponse,
  { page?: number },
  { state: AppState; rejectValue: { detail: string } }
>(
  'adminContactUs/getList',
  async ({ page = 1 }, { getState, rejectWithValue }) => {
    const state = getState();
    const token = state.user.authToken;
    if (!state.user.isStaff || !token) {
      return rejectWithValue({ detail: 'Permission denied.' });
    }
    try {
      const params = new URLSearchParams({ page: page.toString() });
      return await sendGetRequest(`/api/admin/contact-us/?${params}`, token);
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Failed to fetch submissions.';
      return rejectWithValue({ detail: message });
    }
  }
);

export const getAdminContactUsUnreadCount = createAsyncThunk<
  { unread_count: number },
  void,
  { state: AppState; rejectValue: { detail: string } }
>(
  'adminContactUs/getUnreadCount',
  async (_, { getState, rejectWithValue }) => {
    const state = getState();
    const token = state.user.authToken;
    if (!state.user.isStaff || !token) {
      return rejectWithValue({ detail: 'Permission denied.' });
    }
    try {
      return await sendGetRequest('/api/admin/contact-us/unread-count/', token);
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Failed to fetch unread count.';
      return rejectWithValue({ detail: message });
    }
  }
);

export const adminMarkAllContactUsRead = createAsyncThunk<
  { detail: string; updated_count: number },
  void,
  { state: AppState; rejectValue: { detail: string } }
>(
  'adminContactUs/markAllRead',
  async (_, { getState, rejectWithValue }) => {
    const state = getState();
    const token = state.user.authToken;
    if (!state.user.isStaff || !token) {
      return rejectWithValue({ detail: 'Permission denied.' });
    }
    try {
      return await sendPostRequest('/api/admin/contact-us/mark-all-read/', {}, token);
    } catch (error: any) {
      const message = error?.detail || 'Failed to mark all as read.';
      return rejectWithValue({ detail: message });
    }
  }
);

export const adminMarkAllContactUsUnread = createAsyncThunk<
  { detail: string; updated_count: number },
  void,
  { state: AppState; rejectValue: { detail: string } }
>(
  'adminContactUs/markAllUnread',
  async (_, { getState, rejectWithValue }) => {
    const state = getState();
    const token = state.user.authToken;
    if (!state.user.isStaff || !token) {
      return rejectWithValue({ detail: 'Permission denied.' });
    }
    try {
      return await sendPostRequest('/api/admin/contact-us/mark-all-unread/', {}, token);
    } catch (error: any) {
      const message = error?.detail || 'Failed to mark all as unread.';
      return rejectWithValue({ detail: message });
    }
  }
);

export const adminDeleteContactUs = createAsyncThunk<
  { contactId: number },
  number,
  { state: AppState; rejectValue: { detail: string } }
>(
  'adminContactUs/delete',
  async (contactId, { getState, rejectWithValue }) => {
    const state = getState();
    const token = state.user.authToken;
    if (!state.user.isStaff || !token) {
      return rejectWithValue({ detail: 'Permission denied.' });
    }
    try {
      await sendDeleteRequest(`/api/admin/contact-us/${contactId}/`, token);
      return { contactId };
    } catch (error: any) {
      const message = error?.detail || 'Failed to delete submission.';
      return rejectWithValue({ detail: message });
    }
  }
);

// Pass a bare id to flip is_read, or { contactId, isRead } to set it (used
// when opening a row, so an already-read row isn't flipped back to unread).
export type ToggleReadArg = number | { contactId: number; isRead: boolean };

export const toggleReadContactId = (arg: ToggleReadArg) =>
  typeof arg === 'number' ? arg : arg.contactId;

export const toggleReadTarget = (arg: ToggleReadArg): boolean | undefined =>
  typeof arg === 'number' ? undefined : arg.isRead;

export const adminToggleReadContactUs = createAsyncThunk<
  ToggleReadResponse,
  ToggleReadArg,
  { state: AppState; rejectValue: { detail: string } }
>(
  'adminContactUs/toggleRead',
  async (arg, { getState, rejectWithValue }) => {
    const contactId = toggleReadContactId(arg);
    const isRead = toggleReadTarget(arg);
    const state = getState();
    const token = state.user.authToken;
    if (!state.user.isStaff || !token) {
      return rejectWithValue({ detail: 'Permission denied.' });
    }
    try {
      return await sendPatchRequest(
        `/api/admin/contact-us/${contactId}/toggle-read/`,
        isRead === undefined ? {} : { is_read: isRead },
        token
      );
    } catch (error: any) {
      const message = error?.detail || 'Failed to update submission.';
      return rejectWithValue({ detail: message });
    }
  }
);
